/**
 * OffboardingInsightsCards — Phase 16 (additive, role-aware)
 * ===========================================================
 *
 * Renders the two new Insights cards:
 *
 *   1. Offboarded Properties        → fully completed offboarding workflows
 *   2. Offboarding Request Pending  → in-progress pending steps actionable
 *                                     by the caller's role
 *
 * Dropped into role insights pages (gtm_sales, gtm_sales_leader, bfr, cx,
 * cx_leader, executive). Backend scoping is enforced server-side; this
 * component only renders whatever the API returns for the caller.
 *
 * Pending card hover behaviour: shows a step-by-step breakdown tooltip
 * (e.g. CX sees ARI Freeze / CX Review / NOC counts). Backend already
 * applies the "no-duplicate" rule per the spec — each lifecycle is
 * bucketed into exactly its first pending step.
 *
 * Card click → drilldown table with an "Open Lifecycle" action that
 * deep-links to the property's offboarding workflow on the exact step.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut, Hourglass, Loader2, X, ExternalLink, RefreshCw,
} from 'lucide-react';

const ClickableInsightCard = ({ label, value, Icon, accent, testId, onClick, hoverContent }) => (
  <div className="relative group" data-testid={`${testId}-wrap`}>
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full text-left bg-white rounded-2xl shadow-sm border border-gray-200 px-5 py-4 hover:shadow-md hover:border-teal-300 transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent.bg}`}>
          <Icon size={18} className={accent.fg} />
        </div>
      </div>
    </button>
    {hoverContent && (
      <div
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-20 w-60 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white rounded-lg shadow-xl px-3 py-2 text-xs"
        data-testid={`${testId}-tooltip`}
      >
        {hoverContent}
      </div>
    )}
  </div>
);

const OffboardingDrilldownModal = ({ title, endpoint, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load detail');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [endpoint]);

  const handleOpen = (row) => {
    onClose();
    // For offboarded properties → land on lifecycle detail. For pending
    // rows → land on offboarding workflow with focus_step query param so
    // the workflow panel auto-expands the correct step.
    if (row?.current_step && row.current_step !== 'Completed') {
      navigate(`/hotel-lifecycle/offboarding/${row.lifecycle_id}?focus_step=${row.current_step}`);
    } else {
      navigate(`/hotel-lifecycle/${row.lifecycle_id || row.property_id}`);
    }
  };

  const columns = data?.columns || [];
  const rows = data?.rows || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="offboarding-drilldown-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[80vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
          <div>
            <h3 className="font-bold text-gray-800 text-base">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {loading ? 'Loading…' : `${rows.length} row${rows.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="offboarding-drilldown-close"
            className="p-1.5 hover:bg-gray-200 rounded-lg"
            aria-label="Close"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 size={22} className="animate-spin" />
            </div>
          ) : error ? (
            <div className="m-5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-12">No data for this card right now.</p>
          ) : (
            <table className="w-full text-sm" data-testid="offboarding-drilldown-table">
              <thead className="bg-gray-50 sticky top-0 text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className="px-4 py-2.5 text-left font-semibold whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                  <th className="px-4 py-2.5 text-right font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => (
                  <tr
                    key={`${row.lifecycle_id || i}-${i}`}
                    className="hover:bg-gray-50"
                    data-testid={`offboarding-drilldown-row-${i}`}
                  >
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-2.5 text-gray-700 whitespace-nowrap">
                        {row[c.key] ?? '—'}
                      </td>
                    ))}
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => handleOpen(row)}
                        data-testid={`offboarding-drilldown-open-${i}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100"
                      >
                        <ExternalLink size={12} />
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const OffboardingInsightsCards = () => {
  const [offboarded, setOffboarded] = useState({ count: 0 });
  const [pending, setPending] = useState({ count: 0, breakdown: [] });
  const [loading, setLoading] = useState(true);
  const [drilldown, setDrilldown] = useState(null); // { title, endpoint }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [obRes, peRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-insights/offboarding/offboarded`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-insights/offboarding/pending`, { headers }),
      ]);
      if (obRes.ok) setOffboarded(await obRes.json());
      if (peRes.ok) setPending(await peRes.json());
    } catch {
      /* silent — empty state is the safe default */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Lightweight refresh on focus so completing a step elsewhere updates
    // these counts as soon as the user comes back to Insights.
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const pendingTooltip = pending.breakdown && pending.breakdown.length > 0 ? (
    <div data-testid="offboarding-pending-breakdown">
      {pending.breakdown.map((b) => (
        <div key={b.step_id} className="flex items-center justify-between py-0.5">
          <span className="text-gray-300">{b.label}</span>
          <span
            className="font-bold text-amber-300"
            data-testid={`offboarding-pending-breakdown-${b.step_id}`}
          >
            {b.count}
          </span>
        </div>
      ))}
      <div className="mt-1 pt-1 border-t border-gray-700 flex items-center justify-between">
        <span className="text-gray-300">Total</span>
        <span className="font-bold text-white" data-testid="offboarding-pending-breakdown-total">
          {pending.count}
        </span>
      </div>
    </div>
  ) : null;

  return (
    <div className="space-y-3" data-testid="offboarding-insights-cards">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
          Offboarding
        </h3>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-teal-600 disabled:opacity-50"
          data-testid="offboarding-insights-refresh"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ClickableInsightCard
          label="Offboarded Properties"
          value={offboarded.count ?? 0}
          Icon={LogOut}
          accent={{ bg: 'bg-rose-50', fg: 'text-rose-600' }}
          testId="card-offboarded-properties"
          onClick={() => setDrilldown({
            title: 'Offboarded Properties',
            endpoint: '/api/role-insights/offboarding/offboarded',
          })}
        />
        <ClickableInsightCard
          label="Offboarding Request Pending"
          value={pending.count ?? 0}
          Icon={Hourglass}
          accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }}
          testId="card-offboarding-pending"
          onClick={() => setDrilldown({
            title: 'Offboarding Request Pending',
            endpoint: '/api/role-insights/offboarding/pending',
          })}
          hoverContent={pendingTooltip}
        />
      </div>

      {drilldown && (
        <OffboardingDrilldownModal
          title={drilldown.title}
          endpoint={drilldown.endpoint}
          onClose={() => {
            setDrilldown(null);
            // Re-pull on close — covers the case where the user navigated
            // away, actioned a step, and came back to the modal-closing
            // refresh path.
            load();
          }}
        />
      )}
    </div>
  );
};

export default OffboardingInsightsCards;
